import React, { useMemo, useState } from "react";
import {
	Alert,
	Button,
	DatePicker,
	Input,
	InputNumber,
	Radio,
	Table,
	Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import useLang from "../../../../hooks/useLang";
import {
	ClientStoppedOrdering,
	ClientsStoppedOrderingReport,
	SortDirection,
	StoppedOrderingQuery,
	exportClientsStoppedOrdering,
	getClientsStoppedOrdering,
	readBlobErrorMessage,
	saveWorkbook,
} from "../../../../api/client-reports";
import BulkOpsHeader from "../../components/BulkOpsHeader";
import { PRIMARY_BUTTON_STYLE } from "../../components/styles";
import {
	countryTotalsColumns,
	daysTagColor,
	formatDate,
	formatDateTime,
	formatNumber,
	identityColumns,
	toApiSort,
	toWireDate,
} from "../reportFormat";

const { RangePicker } = DatePicker;

const DEFAULT_PAGE_SIZE = 50;

/**
 * The API rejects month/year combined with lastOrderFrom/lastOrderTo, so the two are offered as
 * mutually exclusive modes rather than as fields the operator can fill in at the same time and
 * get a 400 for.
 */
type WindowMode = "range" | "month" | "year";

const StoppedOrdering = () => {
	const { strings } = useLang("clientReports");
	const common = strings?.common;
	const copy = strings?.stoppedOrdering;
	const reportStrings = strings?.reports?.stoppedOrdering;

	const [windowMode, setWindowMode] = useState<WindowMode>("range");
	// Defaults to the API's own one-year lookback, shown explicitly so the operator can see the
	// window rather than wonder what an empty date range means.
	const [range, setRange] = useState<[Dayjs, Dayjs] | null>([
		dayjs().subtract(1, "year"),
		dayjs(),
	]);
	const [month, setMonth] = useState<Dayjs | null>(dayjs());
	const [year, setYear] = useState<Dayjs | null>(dayjs());
	const [minDays, setMinDays] = useState<number | null>(1);
	const [maxDays, setMaxDays] = useState<number | null>(null);
	const [search, setSearch] = useState("");

	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [report, setReport] = useState<ClientsStoppedOrderingReport | null>(
		null
	);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	// The filter set that produced the result on screen. Paging and sorting re-query with
	// THIS, not with the controls above, so editing a filter without pressing Generate can
	// never leak into a page change.
	const [appliedQuery, setAppliedQuery] =
		useState<StoppedOrderingQuery | null>(null);
	const [sortBy, setSortBy] = useState<string | undefined>();
	const [sortDirection, setSortDirection] = useState<
		SortDirection | undefined
	>();

	/** Everything except paging and sorting — the part the export shares with the grid. */
	const filterQuery = useMemo((): StoppedOrderingQuery => {
		const base: StoppedOrderingQuery = {
			minDaysWithoutOrders: minDays ?? undefined,
			maxDaysWithoutOrders: maxDays ?? undefined,
			search: search.trim() || undefined,
		};

		if (windowMode === "month" && month) {
			return { ...base, month: month.month() + 1, year: month.year() };
		}
		if (windowMode === "year" && year) {
			return { ...base, year: year.year() };
		}
		return {
			...base,
			lastOrderFrom: range?.[0] ? toWireDate(range[0]) : undefined,
			lastOrderTo: range?.[1] ? toWireDate(range[1]) : undefined,
		};
	}, [windowMode, range, month, year, minDays, maxDays, search]);

	const load = async (
		query: StoppedOrderingQuery,
		nextPage: number,
		nextPageSize: number,
		nextSortBy?: string,
		nextSortDirection?: SortDirection
	) => {
		setLoading(true);
		try {
			const result = await getClientsStoppedOrdering({
				...query,
				currentPage: nextPage,
				pageSize: nextPageSize,
				sortBy: nextSortBy,
				sortDirection: nextSortDirection,
			});
			setReport(result);
			setAppliedQuery(query);
			setPage(nextPage);
			setPageSize(nextPageSize);
		} catch {
			// The axios interceptor already toasts the API's message.
		} finally {
			setLoading(false);
		}
	};

	/** A filter change invalidates the page number — page 7 of the old filter means nothing. */
	const generate = () => {
		load(filterQuery, 1, pageSize, sortBy, sortDirection);
	};

	const exportXlsx = async () => {
		if (!appliedQuery) {
			return;
		}

		setExporting(true);
		try {
			const blob = await exportClientsStoppedOrdering({
				...appliedQuery,
				sortBy,
				sortDirection,
			});
			saveWorkbook(
				blob,
				`kliente-te-ndaluar_${dayjs().format("YYYY-MM-DD")}.xlsx`
			);
		} catch (error) {
			// A failed blob request hides its JSON body from the interceptor, so the real reason
			// (for example the 50 000-row cap) has to be read out of the Blob here.
			const message = await readBlobErrorMessage(error);
			if (message) {
				toast.error(message);
			}
		} finally {
			setExporting(false);
		}
	};

	const labels = {
		code: common?.colCode ?? "Kodi i klientit",
		name: common?.colName ?? "Emri dhe mbiemri",
		business: common?.colBusiness ?? "Biznesi",
		phone: common?.colPhone ?? "Telefoni",
		businessPhone: common?.colBusinessPhone ?? "Telefoni i biznesit",
		clientSince: common?.colClientSince ?? "Klient që nga",
		totalOrders: common?.colTotalOrders ?? "Totali i porosive",
		countrySplit: common?.colCountrySplit ?? "Sipas shtetit",
		unknownCountryHint:
			common?.unknownCountryHint ??
			"Porosi që nuk lidhen me asnjë shtet — zona e tyre nuk zgjidhet deri te shteti. Vlen të kontrollohen.",
	};

	const columns = useMemo<ColumnsType<ClientStoppedOrdering>>(
		() => [
			...identityColumns<ClientStoppedOrdering>(labels),
			{
				title: copy?.colLastOrder ?? "Porosia e fundit",
				dataIndex: "lastOrderDate",
				key: "lastOrderDate",
				width: 150,
				sorter: true,
				defaultSortOrder: "descend",
				render: (value: string) => formatDateTime(value),
			},
			{
				title: copy?.colInactiveSince ?? "Joaktiv nga",
				dataIndex: "inactiveSince",
				key: "inactiveSince",
				width: 120,
				render: (value: string) => formatDate(value),
			},
			{
				title: copy?.colDaysWithout ?? "Ditë pa porosi",
				dataIndex: "daysWithoutOrders",
				key: "daysWithoutOrders",
				align: "right",
				width: 130,
				sorter: true,
				render: (days: number) => (
					<Tag color={daysTagColor(days)}>{formatNumber(days)}</Tag>
				),
			},
			...countryTotalsColumns<ClientStoppedOrdering>(labels),
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[copy, common]
	);

	const rows = report?.page.items ?? [];
	const hasRun = report !== null;

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={
					reportStrings?.label ?? "Klientë që kanë ndaluar porositë"
				}
				subtitle={
					reportStrings?.description ??
					"Klientët që nuk kanë sjellë më porosi — listë për t'i kontaktuar"
				}
				backTo="/bulk-order-operations/client-reports"
				backLabel={common?.back ?? "Të gjitha raportet"}
				actions={
					<Button
						icon={<DownloadOutlined />}
						loading={exporting}
						disabled={!hasRun || rows.length === 0}
						onClick={exportXlsx}
					>
						{common?.exportExcel ?? "Shkarko Excel"}
					</Button>
				}
			/>

			<Alert
				type="info"
				showIcon
				message={
					copy?.scopeNote ??
					"Filtron porosinë e FUNDIT ndonjëherë të klientit. Përfshihen vetëm klientët aktivë e të aprovuar; porositë e nënklientëve numërohen te klienti prind."
				}
			/>

			<div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<span className="font-medium">
						{copy?.windowLabel ?? "Kur ishte porosia e fundit"}
					</span>
					<Radio.Group
						value={windowMode}
						onChange={(e) => setWindowMode(e.target.value)}
						optionType="button"
						buttonStyle="solid"
					>
						<Radio.Button value="range">
							{copy?.modeRange ?? "Periudhë"}
						</Radio.Button>
						<Radio.Button value="month">
							{copy?.modeMonth ?? "Muaj"}
						</Radio.Button>
						<Radio.Button value="year">
							{copy?.modeYear ?? "Vit"}
						</Radio.Button>
					</Radio.Group>

					<div>
						{windowMode === "range" && (
							<RangePicker
								value={range}
								format="DD/MM/YYYY"
								className="w-full md:w-auto"
								disabledDate={(current) =>
									current && current.isAfter(dayjs(), "day")
								}
								onChange={(value) =>
									setRange(
										value?.[0] && value?.[1]
											? [value[0], value[1]]
											: null
									)
								}
							/>
						)}
						{windowMode === "month" && (
							<DatePicker
								picker="month"
								value={month}
								format="MM/YYYY"
								allowClear={false}
								disabledDate={(current) =>
									current && current.isAfter(dayjs(), "month")
								}
								onChange={(value) => setMonth(value)}
							/>
						)}
						{windowMode === "year" && (
							<DatePicker
								picker="year"
								value={year}
								allowClear={false}
								disabledDate={(current) =>
									current && current.isAfter(dayjs(), "year")
								}
								onChange={(value) => setYear(value)}
							/>
						)}
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<label className="flex flex-col gap-1">
						<span className="text-sm text-gray-600">
							{copy?.minDaysLabel ?? "Së paku ditë pa porosi"}
						</span>
						<InputNumber
							min={0}
							value={minDays}
							onChange={setMinDays}
							className="w-full"
							placeholder="1"
						/>
						<span className="text-xs text-gray-400">
							{copy?.minDaysHint ?? "1 = nuk ka porositur sot"}
						</span>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-sm text-gray-600">
							{copy?.maxDaysLabel ??
								"Më së shumti ditë pa porosi"}
						</span>
						<InputNumber
							min={0}
							value={maxDays}
							onChange={setMaxDays}
							className="w-full"
							placeholder={copy?.optional ?? "Opsionale"}
						/>
						<span className="text-xs text-gray-400">
							{copy?.maxDaysHint ??
								"p.sh. 90 për të parë vetëm klientët e humbur së fundmi"}
						</span>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-sm text-gray-600">
							{common?.searchLabel ?? "Kërko klient"}
						</span>
						<Input
							allowClear
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onPressEnter={generate}
							prefix={<SearchOutlined />}
							placeholder={
								common?.searchPlaceholder ??
								"Emri, biznesi, telefoni ose kodi"
							}
						/>
					</label>
				</div>

				<div>
					<Button
						type="primary"
						style={PRIMARY_BUTTON_STYLE}
						loading={loading}
						onClick={generate}
					>
						{common?.generate ?? "Gjenero raportin"}
					</Button>
				</div>
			</div>

			{hasRun && (
				<div className="rounded-lg border border-[#4F16C2] bg-[#4F16C2]/5 p-3 flex flex-wrap gap-6 text-sm">
					<span>
						{copy?.summaryClients ?? "Klientë të gjetur"}:{" "}
						<b>{formatNumber(report.page.totalCount)}</b>
					</span>
					<span>
						{copy?.summaryOrders ?? "Porosi që humbëm"}:{" "}
						<b>{formatNumber(report.totalOrders)}</b>
					</span>
					<span>
						{copy?.summaryWindow ?? "Periudha"}:{" "}
						<b>
							{formatDate(report.lastOrderFrom)} —{" "}
							{report.lastOrderToExclusive
								? formatDate(
										dayjs(report.lastOrderToExclusive)
											.subtract(1, "day")
											.toISOString()
								  )
								: formatDate(report.generatedAt)}
						</b>
					</span>
				</div>
			)}

			<Table<ClientStoppedOrdering>
				rowKey="sellerId"
				size="small"
				columns={columns}
				dataSource={rows}
				loading={loading}
				scroll={{ x: 1400 }}
				onChange={(pagination, _filters, sorter) => {
					if (!appliedQuery) {
						return;
					}

					const { sortBy: nextSortBy, sortDirection: nextDirection } =
						toApiSort(
							sorter as SorterResult<ClientStoppedOrdering>
						);

					setSortBy(nextSortBy);
					setSortDirection(nextDirection);
					load(
						appliedQuery,
						pagination.current ?? 1,
						pagination.pageSize ?? pageSize,
						nextSortBy,
						nextDirection
					);
				}}
				pagination={{
					current: page,
					pageSize,
					total: report?.page.totalCount ?? 0,
					showSizeChanger: true,
					// The API caps pageSize at 500 and rejects anything larger with a 400.
					pageSizeOptions: [20, 50, 100, 200, 500],
					showTotal: (total) =>
						`${formatNumber(total)} ${
							common?.clients ?? "klientë"
						}`,
				}}
				locale={{
					emptyText: hasRun
						? copy?.noRows ??
						  "Asnjë klient nuk përputhet me këta filtra"
						: common?.notGenerated ??
						  "Zgjidh filtrat dhe kliko Gjenero raportin",
				}}
			/>
		</div>
	);
};

export default StoppedOrdering;
